/**
 * ReportabilityPanel (13-27): the reportability call on an incident, FIELDSET §3.3.1's reportability verb (8 columns),
 * built from the 13-10 regulatory panel's parts (D-13-02-1, D-13-10-1, D-13-10-3).
 *
 * - **Context first, on one screen:** the report's own facts (summary, business unit, when it happened, was reported
 *   and became known, whether it occurred or is alleged, the harm, the client, category, what was done, the Care
 *   Manager's triage note, severity), and the C3 card's draft collapsed under "The agent's draft, as written", one
 *   click away and never a nudge.
 * - **The decision:** the live `ReportabilityDecision` values as neutral option cards (grey borders, nothing
 *   pre-selected, no "Agree", a charcoal save). The detail columns follow the chosen option (SIRS: kind, P1 ground,
 *   neglect exclusion; NDIS: kind, class). `AwareAt` is a Melbourne date and time.
 * - **The deadline is computed, never typed** (D-13-L-11): `NotificationDueAt` is shown as the form fills in, with
 *   each rule's OB row and section; "Override" takes a date, a time and its own reason. The engine computes the same
 *   value at save (D-13-27-3), so the shown and the stored deadline can't differ.
 * - **A reason on every save** (at least 10 characters), on every `StateChange` row. `Pending` is an answer when it
 *   is chosen with a reason (D-13-27-4).
 * - **Checks against the report** (`reportPrompts`): one plain line asking the decider to confirm. Never a block.
 * - **Who** (D-13-L-2): the SaH decider on an aged-care record, any Oversight member otherwise. Anyone else with a
 *   register role reads it here, with who decides and whether it is still pending (never hidden).
 * Practice mode: the runtime's practice writer stores nothing on SharePoint.
 */

import { useMemo, useState } from 'react';
import VerbForm from '../VerbForm';
import type { VerbBuilder, VerbControlProps, VerbFormControl } from '../VerbForm';
import Lines from '../Lines';
import { Button, Input, TextArea } from '../../ui';
import { choicesOf, fieldOf } from '../../../config/fieldset/registerFields';
import { formatInstant, todayMelbourne } from '../../../lib/fieldValues';
import { PENDING_DECISION } from '../../../lib/notificationDeadlines';
import type { DeadlineRule, DueResult } from '../../../lib/notificationDeadlines';
import { reportabilityWords } from '../../../lib/options';
import { canDoRecordVerb } from '../../../lib/recordAuthority';
import { RECORD_CONTEXT_READS } from '../../../lib/recordQuery';
import { useRecordContext } from '../../../hooks/useRecordContext';
import { valueOf } from '../../../lib/recordView';
import { buildRecordVerb } from '../../../lib/recordVerbs';
import { NOT_RECORDED } from '../../../lib/verbForm';
import type { FormValues } from '../../../lib/verbForm';
import {
  DECISION,
  DUE_AT,
  REGIME_WORDS,
  decidesThisOne,
  dueOutcome,
  formDue,
  reportPrompts,
  reportabilityInputs,
  reportabilityShows,
} from '../../../lib/regulatoryPanels';
import type { RecordPatch, RecordRow } from '../../../types/records';
import type { PanelProps } from './registry';
import record from '../record.module.css';
import reg from '../../today/today.module.css';
import styles from './ReportabilityPanel.module.css';

const I = 'incidents';

/** The report's facts the decider reads first (the plan's list), each a field-set column (checked at load). */
const CONTEXT: readonly string[] = [
  'Title',
  'BusinessUnit',
  'OccurredOn',
  'ReportedOn',
  'AwareAt',
  'IncidentBasis',
  'HarmKind',
  'HarmDescription',
  'RecipientName',
  'Category',
  'ImmediateActions',
  'TriageNote',
  'Severity',
];
for (const column of CONTEXT) if (!fieldOf(I, column)) throw new Error(`ReportabilityPanel: ${column} is not an Incidents column`);

/** The C3 card's draft (read with the record, 13-27 `RECORD_CONTEXT_READS`): words for each, by column. */
const DRAFT_WORDS: Readonly<Record<string, string>> = {
  ReportabilityAssessment: 'Assessment',
  Confidence: 'Confidence',
  DraftSeverity: 'Draft severity',
};
for (const column of RECORD_CONTEXT_READS.incidents ?? []) {
  if (!DRAFT_WORDS[column]) throw new Error(`ReportabilityPanel: no words for the draft column ${column}`);
}


const MIN_REASON = 10;

function text(value: unknown): string {
  return typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value);
}

const DRAFT: readonly string[] = RECORD_CONTEXT_READS.incidents ?? [];

/** The report's facts, then the agent's draft collapsed (D-13-10-1), read only when it is opened. */
function ContextBlock({ row }: { row: RecordRow }) {
  const [open, setOpen] = useState(false);
  const context = useRecordContext(row, DRAFT, open);
  return (
    <div className={styles.context}>
      <h4 className={styles.subTitle}>What the report says</h4>
      <dl className={record.facts}>
        {CONTEXT.map((column) => {
          const spec = fieldOf(I, column)!;
          const v = valueOf(spec, row);
          return (
            <div key={column} style={{ display: 'contents' }}>
              <dt>{spec.label}</dt>
              <dd>{v.value === null ? <span className={record.blank}>{NOT_RECORDED}</span> : spec.kind === 'textarea' ? <Lines text={v.value} /> : v.value}</dd>
            </div>
          );
        })}
      </dl>
      <details className={reg.raw} onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
        <summary>The agent&rsquo;s draft, as written</summary>
        {context.status === 'loading' && <p className={record.hint}>Reading the draft…</p>}
        {context.status === 'error' && (
          <p className={record.hint} role="status">
            The draft couldn&rsquo;t be read ({context.error}). The decision doesn&rsquo;t depend on it.
          </p>
        )}
        <dl className={record.facts}>
          {DRAFT.map((column) => {
            const raw = text(context.fields[column]).trim();
            return (
              <div key={column} style={{ display: 'contents' }}>
                <dt>{DRAFT_WORDS[column]}</dt>
                <dd>{raw === '' ? <span className={record.blank}>{NOT_RECORDED}</span> : column === 'Confidence' ? `${raw}%` : <Lines text={raw} />}</dd>
              </div>
            );
          })}
        </dl>
        <p className={record.hint}>This is the C3 card&rsquo;s draft and may include the agent&rsquo;s view. The choices below are listed without it.</p>
      </details>
    </div>
  );
}

/** One rule's line: the regulator, the due instant, how it is worked out, and its citation. */
function RuleLine({ rule }: { rule: DeadlineRule }) {
  return (
    <li>
      <strong>{REGIME_WORDS[rule.regime]}:</strong> {formatInstant(rule.dueAt)}, {rule.reading}.{' '}
      <span className={styles.cite}>
        {rule.obRow ?? 'No obligations row'} · {rule.section}
      </span>
      {rule.note && <span className={styles.ruleNote}> {rule.note}</span>}
      {rule.assumed && <span className={styles.ruleNote}> {rule.assumed}</span>}
      {rule.provisional && <span className={styles.ruleNote}> Provisional: {rule.provisional}.</span>}
    </li>
  );
}

interface Override {
  open: boolean;
  day: string;
  time: string;
  reason: string;
}

const CLOSED: Override = { open: false, day: '', time: '', reason: '' };

function DueBlock({
  row,
  values,
  control,
  override,
  setOverride,
}: {
  row: RecordRow;
  values: FormValues;
  control: VerbFormControl;
  override: Override;
  setOverride: (o: Override) => void;
}) {
  const decision = values[DECISION] ?? '';
  const [today] = useState(() => todayMelbourne(new Date()));
  if (!decision) {
    return (
      <div className={styles.due} aria-live="polite">
        <p className={record.label}>Notification due</p>
        <p className={record.hint}>Choose a decision to see its notification deadline.</p>
      </div>
    );
  }
  const due: DueResult = formDue(row, values);
  const outcome = dueOutcome(row, decision, due);
  const stored = text(row.fields[DUE_AT]);
  const change = (next: Override) => {
    setOverride(next);
    control.invalidate();
  };
  const refused = control.refusal('dueOverride');
  return (
    <div className={styles.due} aria-live="polite">
      <p className={record.label}>Notification due</p>
      {outcome === 'set' && due.dueAt && (
        <>
          <p className={styles.dueValue}>
            {formatInstant(due.dueAt)}
            {override.open ? <span className={styles.ruleNote}> (computed; overridden below)</span> : null}
          </p>
          <p className={record.hint}>Worked out from the record when you save; never typed.{due.rules.length > 1 ? ' The earliest is stored.' : ''}</p>
          <ul className={styles.rules}>
            {due.rules.map((r) => (
              <RuleLine key={r.key} rule={r} />
            ))}
          </ul>
        </>
      )}
      {outcome === 'missing' && <p className={record.hint}>Enter when Harmony became aware to start the clock.</p>}
      {outcome === 'keep' && (
        <p className={record.hint}>No deadline while the decision is pending. The earlier due time ({formatInstant(stored)}) stays on the record.</p>
      )}
      {outcome === 'clear' && <p className={record.hint}>Not reportable: no notification deadline. The earlier due time ({formatInstant(stored)}) is cleared when you save.</p>}
      {outcome === 'none' && (
        <p className={record.hint}>{decision === PENDING_DECISION ? 'No deadline while the decision is pending.' : 'No notification deadline for this decision.'}</p>
      )}

      {outcome === 'set' && !override.open && (
        <Button type="button" variant="ghost" size="sm" onClick={() => change({ ...CLOSED, open: true })}>
          Override
        </Button>
      )}
      {outcome === 'set' && override.open && (
        <div className={styles.override}>
          <p className={record.hint}>A different deadline, for example a period the Commissioner set. It needs its own reason, which goes on the trail.</p>
          <div className={record.dateRow}>
            <Input type="date" aria-label="Overridden deadline: date" value={override.day} className={record.dateInput} onChange={(e) => change({ ...override, day: e.target.value })} />
            <Input type="time" aria-label="Overridden deadline: time" value={override.time} className={record.timeInput} onChange={(e) => change({ ...override, time: e.target.value })} />
            <Button type="button" variant="ghost" size="sm" onClick={() => change({ ...override, day: today })}>
              Today
            </Button>
          </div>
          <label className={record.label} htmlFor="rep-override-reason">
            Why the deadline is different
          </label>
          <TextArea id="rep-override-reason" rows={2} value={override.reason} onChange={(e) => change({ ...override, reason: e.target.value })} />
          <p className={record.hint}>At least {MIN_REASON} characters.</p>
          {refused && <p className={record.refusal}>{refused}</p>}
          <Button type="button" variant="secondary" size="sm" onClick={() => change(CLOSED)}>
            Use the computed time
          </Button>
        </div>
      )}

      {reportPrompts(row, decision).map((line) => (
        <p key={line} className={record.notice} role="status">
          {line}
        </p>
      ))}
    </div>
  );
}

/** The neutral option cards (D-13-02-1): the live values, nothing chosen for you. */
function DecisionOptions({ control, label }: { control: VerbControlProps; label: string }) {
  return (
    <fieldset className={`${reg.opts} ${control.refused ? styles.refusedGroup : ''}`} aria-label={label} aria-describedby={control.describedBy}>
      <legend className={reg.legend}>The options are listed as the register holds them. Nothing is chosen for you.</legend>
      {choicesOf(I, DECISION).map((value, i) => (
        <label key={value} className={reg.optRow}>
          <input
            id={i === 0 ? control.id : `${control.id}-${i}`}
            type="radio"
            name={control.id}
            value={value}
            checked={control.value === value}
            onChange={() => control.set(value)}
          />
          <span>
            <b>{reportabilityWords(value)}</b>
            {reportabilityWords(value) !== value && <span className={styles.code}>{value}</span>}
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function decisionNow(row: RecordRow): { words: string; pending: boolean } {
  const value = text(row.fields[DECISION]).trim();
  const pending = value === '' || value === PENDING_DECISION;
  return { words: value === '' ? 'Not recorded' : reportabilityWords(value), pending };
}

/** Read only (D-13-L-2): who decides, what is on the record, and that it is still pending if it is. */
function ReadOnlyView({ row }: { row: RecordRow }) {
  const now = decisionNow(row);
  const due = text(row.fields[DUE_AT]);
  return (
    <div className={record.verbForm}>
      <p className={styles.who}>
        <strong>{decidesThisOne(row)}</strong> You can read the report and the decision here.
      </p>
      <dl className={record.facts}>
        <dt>{fieldOf(I, DECISION)?.label}</dt>
        <dd>
          {now.words}
          {now.pending && <span className={styles.pending}>Not decided yet</span>}
        </dd>
        <dt>{fieldOf(I, DUE_AT)?.label}</dt>
        <dd>{due ? formatInstant(due) : <span className={record.blank}>{NOT_RECORDED}</span>}</dd>
      </dl>
      <ContextBlock row={row} />
      <div className={reg.opts}>
        {choicesOf(I, DECISION).map((value) => (
          <div key={value} className={`${reg.optRow} ${reg.optRo}`}>
            <b>{reportabilityWords(value)}</b>
          </div>
        ))}
      </div>
      <p className={reg.roNote}>{decidesThisOne(row)} You&rsquo;ll see the decision here once it&rsquo;s recorded.</p>
    </div>
  );
}

function confirmNote(patch: RecordPatch) {
  return (
    <p className={record.confirmNote}>
      <strong>This records a regulatory decision under your name.</strong>
      {DUE_AT in patch ? ' The notification deadline above is stored with it.' : ''}
    </p>
  );
}

export default function ReportabilityPanel({ row, siblings, actor, readOnly, onSaved }: PanelProps) {
  const may = !readOnly && canDoRecordVerb('reportability', row, actor).ok;
  const [override, setOverride] = useState<Override>(CLOSED);
  const columns = useMemo(() => reportabilityInputs(), []);
  const decisionLabel = fieldOf(I, DECISION)?.label ?? DECISION;

  if (!may) return <ReadOnlyView row={row} />;

  const build: VerbBuilder = (r, input, now) =>
    buildRecordVerb(
      'reportability',
      r,
      { upn: actor.upn, ...(actor.lookupId ? { siteUserId: actor.lookupId } : {}) },
      {
        set: input.set,
        ...(input.reason ? { reason: input.reason } : {}),
        ...(override.open ? { dueOverride: { at: { day: override.day, time: override.time }, reason: override.reason } } : {}),
        auditId: input.auditId,
      },
      now,
    );

  const now = decisionNow(row);
  return (
    <VerbForm
      register={I}
      verb="reportability"
      row={row}
      siblings={siblings}
      actor={actor}
      columns={columns}
      build={build}
      unprefilled={[DECISION]}
      showColumn={(column, values) => reportabilityShows(column, values[DECISION] ?? '')}
      renderControl={(column, control) => (column === DECISION ? <DecisionOptions control={control} label={decisionLabel} /> : null)}
      afterFields={(values, control) => <DueBlock row={row} values={values} control={control} override={override} setOverride={setOverride} />}
      reasonPrompt={{
        label: 'Your reason (goes on the trail)',
        hint: `At least ${MIN_REASON} characters. Write it as you would explain it to the regulator. No phone numbers or email addresses.`,
      }}
      confirmNote={confirmNote}
      saveTone="solemn"
      saveLabel="Record the decision"
      onSaved={onSaved}
      lead={
        <>
          <p className={styles.who}>
            <strong>On the record now: {now.words}.</strong>
            {now.pending && <span className={styles.pending}>Not decided yet</span>} You record this call
            {decidesThisOne(row).startsWith('The SaH') ? ', as the SaH reportability decider.' : ', as Oversight.'}
          </p>
          <ContextBlock row={row} />
        </>
      }
    />
  );
}
